import { BulletPointsList, Button, Space, theme } from '@/components';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import {
  type CSSProperties,
  type FC,
  type ReactNode,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  dataSource: ReactNode[];
  threshold: number;
  size?: 'small' | 'medium';
  buttonFontSize?: number;
  canShowMore?: boolean;
  initialOpenState?: boolean;
  ellipsis?: boolean;
  wrapperStyle?: CSSProperties;
};

export const ViewMoreList: FC<Props> = ({
  dataSource,
  threshold,
  size = 'small',
  buttonFontSize,
  canShowMore = true,
  initialOpenState = false,
  ellipsis = false,
  wrapperStyle,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [open, setOpen] = useState<boolean>(initialOpenState);
  const displayedDataSource = open
    ? dataSource
    : dataSource.slice(0, threshold);

  const onToggle = () => {
    setOpen(!open);
  };

  useEffect(() => {
    setOpen(initialOpenState);
  }, [initialOpenState]);

  const fontSize =
    buttonFontSize ?? (size === 'medium' ? token.fontSize : token.fontSizeSM);

  return (
    <>
      {dataSource.length > threshold ? (
        canShowMore ? (
          <Space direction="vertical" size={0} style={wrapperStyle}>
            <BulletPointsList list={displayedDataSource} ellipsis={ellipsis} />

            <Button
              type="link"
              onClick={onToggle}
              style={{ padding: 0, border: 0, fontSize }}
            >
              {open ? (
                <Space>
                  <span>{t('close-items')}</span>
                  <UpOutlined style={{ fontSize }} />
                </Space>
              ) : (
                <Space>
                  <span>{t('open-items')}</span>
                  <DownOutlined style={{ fontSize }} />
                </Space>
              )}
            </Button>
          </Space>
        ) : (
          <BulletPointsList
            list={open ? dataSource : dataSource.slice(0, threshold)}
            ellipsis={ellipsis}
          />
        )
      ) : (
        dataSource.length > 0 && (
          <BulletPointsList list={dataSource} ellipsis={ellipsis} />
        )
      )}
    </>
  );
};
